import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { AddCircleOutline } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { authStore } from '@/store/authStore'
import { ehConsultor, papelPrincipal } from '@/components/layout/menu'
import {
  LancamentoComissao,
  PAPEL_FINANCEIRO,
  RecorrenciaReserva,
  StatusComissao,
} from '@/types/comissao'
import RegistrarAtivacaoDialog, { TipoMarcoDialog } from './RegistrarAtivacaoDialog'

/**
 * Comissões lançadas de uma reserva, com o lançamento manual do operador (Etapa 17).
 *
 * O time comercial definiu em 04/08 que a projeção é apurada **na etapa de negociação** e
 * que, para operações recorrentes, **o operador lança mensalmente** — por isso o formulário
 * pede competência, e não uma data qualquer: cada mês é um lançamento.
 *
 * P5 (AN-PP5 §9): o checkbox "recorrente" SAIU — a recorrência vem da política do produto e o
 * cabeçalho mostra o estado DERIVADO no backend (`GET …/recorrencia`). Nada é calculado aqui:
 * recorrência, competências, totais e estado chegam prontos; erros 409/422 aparecem com a
 * mensagem do backend. "Registrar ativação"/"Encerrar recorrência" são do back-office
 * (OPERADOR+ ou o papel complementar FINANCEIRO); o consultor nunca vê.
 */

const brl = (v: number): string =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const mes = (data: string): string => dayjs(data).format('MM/YYYY')

const CHIP: Record<StatusComissao, { rotulo: string; cor: 'success' | 'info' | 'default' }> = {
  CONFIRMADA: { rotulo: 'Disponível para NF', cor: 'success' },
  PROJETADA: { rotulo: 'Projetada', cor: 'info' },
  CANCELADA: { rotulo: 'Cancelada', cor: 'default' },
}

const mensagemDoBackend = (e: unknown, fallback: string): string => {
  const msg = (e as { message?: unknown } | null | undefined)?.message
  return typeof msg === 'string' && msg ? msg : fallback
}

const statusDoErro = (e: unknown): number | undefined =>
  (e as { status?: number } | null | undefined)?.status

type Carga<T> =
  | { estado: 'carregando' }
  | { estado: 'ok'; dados: T }
  | { estado: 'erro'; mensagem: string }
  | { estado: 'sem-permissao' }

const falha = <T,>(e: unknown, fallback: string): Carga<T> =>
  statusDoErro(e) === 403
    ? { estado: 'sem-permissao' }
    : { estado: 'erro', mensagem: mensagemDoBackend(e, fallback) }

/** Back-office (OPERADOR+) ou o papel complementar FINANCEIRO — nunca a persona consultor. */
const podeRegistrarMarcos = (permissoes: string[] | undefined | null): boolean => {
  if (ehConsultor(permissoes)) return false
  const papel = papelPrincipal(permissoes)
  if (papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR') return true
  return (permissoes ?? []).some((p) => p.toUpperCase() === PAPEL_FINANCEIRO)
}

interface Props {
  oportunidadeId: string
  podeLancar: boolean
}

const ComissoesDaReserva: React.FC<Props> = ({ oportunidadeId, podeLancar }) => {
  const { enqueueSnackbar } = useSnackbar()
  const permissoes = authStore((st) => st.usuario?.permissoes)
  const podeMarcos = podeRegistrarMarcos(permissoes)

  const [lancamentos, setLancamentos] = useState<Carga<LancamentoComissao[]>>({
    estado: 'carregando',
  })
  const [recorrencia, setRecorrencia] = useState<Carga<RecorrenciaReserva>>({
    estado: 'carregando',
  })
  const [aberto, setAberto] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [marco, setMarco] = useState<TipoMarcoDialog | null>(null)

  // Padrão: o mês corrente, que é o caso comum do lançamento mensal.
  const [competencia, setCompetencia] = useState(dayjs().format('YYYY-MM'))
  const [competenciaReferencia, setCompetenciaReferencia] = useState('')
  const [valor, setValor] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [erroLancamento, setErroLancamento] = useState<string | null>(null)
  /**
   * P5 (AN-PP5 §3.1): 409 `COMPETENCIA_FECHADA` traz a sugestão do backend (`data.competenciaSugerida`
   * e `data.competenciaReferenciaSugerida`, `YYYY-MM-DD`) — a tela oferece relançar com ela.
   */
  const [sugestaoCompetencia, setSugestaoCompetencia] = useState<{ competencia: string; referencia: string } | null>(
    null
  )

  const carregarLancamentos = useCallback(async () => {
    setLancamentos({ estado: 'carregando' })
    try {
      setLancamentos({ estado: 'ok', dados: await comissaoService.porReserva(oportunidadeId) })
    } catch (e) {
      setLancamentos(falha(e, 'Não foi possível carregar as comissões desta reserva.'))
    }
  }, [oportunidadeId])

  const carregarRecorrencia = useCallback(async () => {
    setRecorrencia({ estado: 'carregando' })
    try {
      setRecorrencia({
        estado: 'ok',
        dados: await comissaoService.recorrenciaReserva(oportunidadeId),
      })
    } catch (e) {
      setRecorrencia(falha(e, 'Não foi possível carregar a recorrência da comissão.'))
    }
  }, [oportunidadeId])

  const carregar = useCallback(async () => {
    await Promise.all([carregarLancamentos(), carregarRecorrencia()])
  }, [carregarLancamentos, carregarRecorrencia])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const abrirLancamento = () => {
    setErroLancamento(null)
    setAberto(true)
  }

  const lancar = async (sugerida?: { competencia: string; referencia: string }) => {
    setSalvando(true)
    setErroLancamento(null)
    setSugestaoCompetencia(null)
    const mesLancamento = sugerida?.competencia ?? competencia
    const mesReferencia = sugerida?.referencia ?? competenciaReferencia
    if (sugerida) {
      setCompetencia(sugerida.competencia)
      setCompetenciaReferencia(sugerida.referencia)
    }
    try {
      await comissaoService.lancar(oportunidadeId, {
        // O backend normaliza para o dia 1; mandar o dia 1 explícito evita depender de
        // fuso na conversão do input `month`.
        competencia: `${mesLancamento}-01`,
        valorProjetado: Number(valor),
        competenciaReferencia: mesReferencia ? `${mesReferencia}-01` : null,
        observacoes: observacoes.trim() || null,
      })
      enqueueSnackbar('Comissão projetada lançada.', { variant: 'success' })
      setAberto(false)
      setValor('')
      setObservacoes('')
      setCompetenciaReferencia('')
      await carregar()
    } catch (e) {
      // 409/422 do backend (competência fechada, comissão única já lançada, recorrência não
      // ativada, produto sem política…): a mensagem dele diz o que fazer — exibida como vem.
      setErroLancamento(mensagemDoBackend(e, 'Falha ao lançar a comissão.'))
      const corpo = (e as { data?: { errorCode?: unknown; data?: Record<string, unknown> | null } } | null)?.data
      const sugerida = corpo?.data?.competenciaSugerida
      const referencia = corpo?.data?.competenciaReferenciaSugerida
      if (corpo?.errorCode === 'COMPETENCIA_FECHADA' && typeof sugerida === 'string' && typeof referencia === 'string') {
        setSugestaoCompetencia({ competencia: sugerida.slice(0, 7), referencia: referencia.slice(0, 7) })
      }
    } finally {
      setSalvando(false)
    }
  }

  const rec = recorrencia.estado === 'ok' ? recorrencia.dados : null
  const podeAtivar = podeMarcos && Boolean(rec?.confirmacaoAtivacao)
  const podeEncerrar = podeMarcos && rec?.estado === 'RECORRENTE_ATIVA'

  /** Competência (ou referência) do lançamento estornado — busca na própria lista, sem conta. */
  const rotuloEstorno = (l: LancamentoComissao, todos: LancamentoComissao[]): string => {
    const original = todos.find((o) => o.id === l.estornaLancamentoId)
    if (!original) return 'Estorno'
    return `Estorno de ${mes(original.competenciaReferencia ?? original.competencia)}`
  }

  const cabecalhoRecorrencia = () => {
    switch (recorrencia.estado) {
      case 'carregando':
        return (
          <Typography variant="body2" color="textSecondary">
            Carregando a recorrência…
          </Typography>
        )
      case 'sem-permissao':
        return (
          <Typography variant="body2" color="textSecondary">
            Você não tem permissão para ver a recorrência desta reserva.
          </Typography>
        )
      case 'erro':
        return (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => void carregarRecorrencia()}>
                Tentar novamente
              </Button>
            }
          >
            {recorrencia.mensagem}
          </Alert>
        )
      case 'ok':
        return (
          <Typography variant="body2" data-testid="recorrencia-estado">
            Comissão: <strong>{recorrencia.dados.descricaoEstado}</strong>
          </Typography>
        )
    }
  }

  const corpoLancamentos = () => {
    switch (lancamentos.estado) {
      case 'carregando':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} aria-label="Carregando comissões" />
          </Box>
        )
      case 'sem-permissao':
        return (
          <Typography variant="body2" color="textSecondary">
            Você não tem permissão para ver as comissões desta reserva.
          </Typography>
        )
      case 'erro':
        return (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void carregarLancamentos()}>
                Tentar novamente
              </Button>
            }
          >
            {lancamentos.mensagem}
          </Alert>
        )
      case 'ok': {
        const todos = lancamentos.dados
        if (todos.length === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              Nenhuma comissão lançada para esta reserva.
            </Typography>
          )
        }
        return (
          <Stack spacing={1.5}>
            {todos.map((l) => {
              const estorno = l.tipo === 'ESTORNO'
              return (
                <Box key={l.id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {estorno ? `${rotuloEstorno(l, todos)} · ` : ''}
                        {mes(l.competencia)} · {brl(l.valorEfetivo)}
                      </Typography>
                      {l.competenciaReferencia && (
                        <Typography variant="caption" color="textSecondary" component="div">
                          Ref. {mes(l.competenciaReferencia)}
                        </Typography>
                      )}
                      {l.estornado && (
                        <Typography variant="caption" color="textSecondary" component="div">
                          Estornado
                        </Typography>
                      )}
                      {l.status === 'CANCELADA' && l.motivoCancelamento && (
                        <Typography variant="caption" color="textSecondary" component="div">
                          {l.motivoCancelamento}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {l.competenciaFechada && (
                        <Chip size="small" variant="outlined" label="Competência fechada" />
                      )}
                      {estorno ? (
                        <Chip size="small" label="Estorno" />
                      ) : (
                        <Chip
                          size="small"
                          label={CHIP[l.status].rotulo}
                          color={CHIP[l.status].cor}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        )
      }
    }
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Comissão do consultor"
        action={
          <Stack direction="row" spacing={1}>
            {podeAtivar && (
              <Button size="small" onClick={() => setMarco('ATIVACAO')}>
                Registrar ativação
              </Button>
            )}
            {podeEncerrar && (
              <Button size="small" onClick={() => setMarco('ENCERRAMENTO')}>
                Encerrar recorrência
              </Button>
            )}
            {podeLancar && (
              <Button size="small" startIcon={<AddCircleOutline />} onClick={abrirLancamento}>
                Lançar
              </Button>
            )}
          </Stack>
        }
      />
      <CardContent>
        <Box sx={{ mb: 2 }}>{cabecalhoRecorrencia()}</Box>
        {corpoLancamentos()}
      </CardContent>

      <RegistrarAtivacaoDialog
        open={marco !== null}
        tipo={marco ?? 'ATIVACAO'}
        oportunidadeId={oportunidadeId}
        recorrencia={rec}
        onClose={() => setMarco(null)}
        onRegistrado={() => {
          enqueueSnackbar(
            marco === 'ENCERRAMENTO' ? 'Recorrência encerrada.' : 'Ativação registrada.',
            { variant: 'success' }
          )
          setMarco(null)
          void carregar()
        }}
      />

      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="xs">
        <DialogTitle>Lançar comissão projetada</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {erroLancamento && <Alert severity="error">{erroLancamento}</Alert>}
            {sugestaoCompetencia && (
              <Alert
                severity="info"
                action={
                  <Button
                    size="small"
                    color="inherit"
                    disabled={salvando || !valor || Number(valor) <= 0}
                    onClick={() => void lancar(sugestaoCompetencia)}
                  >
                    Relançar com a sugestão
                  </Button>
                }
              >
                Sugestão do backend: lançar em {mes(`${sugestaoCompetencia.competencia}-01`)} com referência{' '}
                {mes(`${sugestaoCompetencia.referencia}-01`)}.
              </Alert>
            )}
            {rec && (
              <Typography variant="body2" color="textSecondary">
                Recorrência da política do produto: {rec.descricaoEstado}
              </Typography>
            )}
            <TextField
              label="Competência"
              type="month"
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Operações recorrentes têm um lançamento por mês."
              fullWidth
            />
            <TextField
              label="Mês de referência (opcional)"
              type="month"
              value={competenciaReferencia}
              onChange={(e) => setCompetenciaReferencia(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Só quando o valor é de uma competência já fechada."
              fullWidth
            />
            <TextField
              label="Valor projetado"
              type="number"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              fullWidth
            />
            <TextField
              label="Observações"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => void lancar()}
            disabled={salvando || !valor || Number(valor) <= 0}
          >
            {salvando ? 'Lançando…' : 'Lançar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default ComissoesDaReserva
